import { ApolloClient, useApolloClient } from '@apollo/client';
import {
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Spacer,
  Spinner,
  Tabs,
  Tab,
  TabList,
  useToast,
} from '@chakra-ui/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useMemo, useState } from 'react';

import { Util } from '@finley-technologies/finley-types';

import {
  Maybe,
  useGetAssetVerificationReceivablesByMetadataQuery,
  useGetAssetVerificationReceivableHistoryLazyQueryGQLType,
  useGetStoredAssetVerificationReceivablesByMetadataQuery,
  useGetIsAssetVerificationRequiredQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import {
  errorToastConfig,
  successToastConfig,
} from '../../common-frontend/constants';
import {
  getMockUiVisibilityMode,
  useCreditFacilityUiVisibility,
} from '../../common-frontend/hooks/useUiVisibility';
import { GET_RECEIVABLE_DOCUMENTATION_UPLOAD_SIGNED_URL } from '../../files/requests/files.gql';
import { presignedPostUpload } from '../../files/ui/utils';
import { UploadDataTapeForm } from '../../manualDataTapeUploads/ui/UploadDataTapeForm';
import { AssetVerificationReceivableHistory } from './AssetVerificationReceivablesHistory';
import { AssetVerificationReceivablesSection } from './AssetVerificationReceivablesTable';
import { EditAssetVerification } from './AssetVerificationReceivablesTable/types';
import { transformAssetVerificationReportToAssetVerificationReceivableContainer } from './AssetVerificationReceivablesTable/utils';
import { FiUpload } from 'react-icons/fi';

// @jendevelops TODO-P4: create generic check component
const CheckAssetVerificationRequired: React.FC<{
  creditFacilityId: string;
  artifactDocumentId: string;
  children: React.ReactNode;
}> = ({ creditFacilityId, artifactDocumentId, children }) => {
  const { data, loading } = useGetIsAssetVerificationRequiredQuery({
    variables: { creditFacilityId, artifactDocumentId },
  });

  if (!data || loading || !data.isAssetVerificationRequired) {
    // @jendevelops TODO-AssetVerification - create skeleton for asset verification section
    return <Skeleton />;
  }

  // need to transform data => add `summaryCounts`, `baseFieldConfigs` can come off a different gql query
  return data.isAssetVerificationRequired && <>{children}</>;
};

enum AssetVerificationSectionKeys {
  newlyAssignedReceivables = 'newlyAssignedReceivables',
  historicalAssignedReceivables = 'historicalAssignedReceivables',
}
type AssetVerificationSectionTabType = {
  key: AssetVerificationSectionKeys;
  label: string;
};
const assetVerificationSectionTabOptions: Record<
  AssetVerificationSectionKeys,
  AssetVerificationSectionTabType
> = {
  newlyAssignedReceivables: {
    key: AssetVerificationSectionKeys.newlyAssignedReceivables,
    label: 'Newly Assigned Receivables',
  },
  historicalAssignedReceivables: {
    key: AssetVerificationSectionKeys.historicalAssignedReceivables,
    label: 'Historical Receivables',
  },
};

type UploadStatus = 'inProgress' | 'none';

const uploadReceivableDocumentation = async ({
  client,
  toast,
  creditFacilityId,
  file,
  uploadStatusCallback,
}: {
  client: ApolloClient<object>;
  toast: ReturnType<typeof useToast>;
  creditFacilityId: string;
  file: File;
  uploadStatusCallback: (status: UploadStatus) => void;
}) => {
  try {
    uploadStatusCallback('inProgress');
    await presignedPostUpload<
      { creditFacilityId: string; filename: string },
      {}
    >({
      client,
      file,
      query: GET_RECEIVABLE_DOCUMENTATION_UPLOAD_SIGNED_URL,
      queryVariables: { creditFacilityId, filename: file.name },
    });
    toast({
      ...successToastConfig,
      title: 'A new asset verification report was uploaded.',
    });
  } catch (err: any) {
    toast({
      ...errorToastConfig,
      title:
        'Error uploading asset verification report. Please try again, and contact your Finley representative if this persists.',
    });
  } finally {
    uploadStatusCallback('none');
  }
};

const AssetVerificationXlsxDocumentationUploadModal: React.FC<{
  creditFacilityId: string;
  isOpen: boolean;
  onClose: () => void;
  uploadStatusCallback: (status: UploadStatus) => void;
}> = ({ creditFacilityId, isOpen, onClose, uploadStatusCallback }) => {
  const client = useApolloClient();
  const toast = useToast();
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Upload New Asset Verification Report</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <UploadDataTapeForm
            allowedExtensions={['xlsx']}
            onClose={onClose}
            handleFile={(file) =>
              uploadReceivableDocumentation({
                client,
                toast,
                file,
                uploadStatusCallback,
                creditFacilityId,
              })
            }
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const AssetVerificationSectionHeader: React.FC<{
  creditFacilityId: string;
}> = withLDConsumer()(({ flags, creditFacilityId }) => {
  // @JonAtFinley @danitian replace with real UiVisibility returned from auth
  const mockUiVisibilityMode = getMockUiVisibilityMode({
    flags,
    defaultTo: 'lenderOwnerWithoutBorrower',
  });
  const facilityUiVisibility = useCreditFacilityUiVisibility({
    mockUiVisibilityMode,
    creditFacilityId,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('none');
  return (
    <>
      <AssetVerificationXlsxDocumentationUploadModal
        creditFacilityId={creditFacilityId}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        uploadStatusCallback={setUploadStatus}
      />
      <Flex alignItems="center">
        <Heading as="h4" fontSize="20px" mb="16px">
          Asset Verification
        </Heading>
        {facilityUiVisibility.verification
          .showAssetVerificationReportUpload && (
          <>
            <Spacer />
            <Button
              leftIcon={
                uploadStatus === 'inProgress' ? (
                  <Spinner size="sm" />
                ) : (
                  <FiUpload />
                )
              }
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(true)}
            >
              Upload New Report
            </Button>
          </>
        )}
      </Flex>
    </>
  );
});

const VerificationAssetVerificationSectionTable: React.FC<{
  creditFacilityId: string;
  artifactDocumentId: string;
  adminMode: boolean;
  assetVerificationReportByMetadata: Maybe<string>;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  onEdit?: EditAssetVerification;
}> = ({
  assetVerificationReportByMetadata,
  creditFacilityId,
  artifactDocumentId,
  reportingDate,
  fileVersion,
  adminMode,
  onEdit = () => {},
}) => {
  const [selectedTab, setSelectedTab] = useState<AssetVerificationSectionKeys>(
    AssetVerificationSectionKeys.newlyAssignedReceivables,
  );

  const [
    getAssetVerificationReceivableHistory,
    {
      data: assetVerificationReceivableHistoryData,
      loading: assetVerificationReceivableHistoryLoading,
    },
  ] = useGetAssetVerificationReceivableHistoryLazyQueryGQLType();

  useEffect(() => {
    if (!adminMode) {
      getAssetVerificationReceivableHistory({
        variables: {
          creditFacilityId,
          reportingDate,
          fileVersion,
          artifactDocumentId,
        },
      });
    }
  }, [
    adminMode,
    artifactDocumentId,
    creditFacilityId,
    fileVersion,
    getAssetVerificationReceivableHistory,
    reportingDate,
  ]);

  const receivablesVerification = useMemo(() => {
    if (!assetVerificationReportByMetadata) {
      return null;
    }
    return transformAssetVerificationReportToAssetVerificationReceivableContainer(
      assetVerificationReportByMetadata,
    );
  }, [assetVerificationReportByMetadata]);

  const tabs = [
    assetVerificationSectionTabOptions.newlyAssignedReceivables,
    assetVerificationSectionTabOptions.historicalAssignedReceivables,
  ];

  // need to transform data => add `summaryCounts`, `baseFieldConfigs` can come off a different gql query
  return (
    <Box mb="40px">
      <AssetVerificationSectionHeader creditFacilityId={creditFacilityId} />
      {!adminMode && (
        <Tabs
          variant="line"
          index={tabs.findIndex(({ key }) => key === selectedTab)}
          colorScheme="blue"
          mt="32px"
          mb="32px"
        >
          <TabList>
            {tabs.map(({ label, key }) => (
              <Tab
                key={key}
                onMouseDown={() => {
                  setSelectedTab(key);
                }}
                fontSize="sm"
                mt="0px"
              >
                {label}
              </Tab>
            ))}
          </TabList>
        </Tabs>
      )}
      {selectedTab ===
        AssetVerificationSectionKeys.newlyAssignedReceivables && (
        <AssetVerificationReceivablesSection
          adminMode={adminMode}
          onEdit={onEdit}
          receivablesVerification={receivablesVerification}
          artifactDocumentId={artifactDocumentId}
          creditFacilityId={creditFacilityId}
          reportingDate={reportingDate}
          fileVersion={fileVersion}
        />
      )}
      {selectedTab ===
        AssetVerificationSectionKeys.historicalAssignedReceivables && (
        <AssetVerificationReceivableHistory
          assetVerificationReceivableHistoryData={
            assetVerificationReceivableHistoryData
          }
          isDataLoading={assetVerificationReceivableHistoryLoading}
        />
      )}
    </Box>
  );
};

const VerificationAssetVerificationSectionTableDynamic: React.FC<{
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
  artifactDocumentId: string;
  fileVersion: number;
  adminMode: boolean;
  loanProductId?: string;
  onEdit?: EditAssetVerification;
}> = ({
  creditFacilityId,
  reportingDate,
  fileVersion,
  artifactDocumentId,
  loanProductId,
  adminMode,
  onEdit = () => {},
}) => {
  const { data, loading } = useGetAssetVerificationReceivablesByMetadataQuery({
    fetchPolicy: 'network-only',
    variables: {
      creditFacilityId,
      reportingDate,
      artifactDocumentId,
      loanProductId,
      fileVersion,
    },
  });

  const assetVerificationReportByMetadata = loading
    ? null
    : data?.assetVerificationReportByMetadata;

  return (
    <VerificationAssetVerificationSectionTable
      creditFacilityId={creditFacilityId}
      artifactDocumentId={artifactDocumentId}
      reportingDate={reportingDate}
      fileVersion={fileVersion}
      adminMode={adminMode}
      assetVerificationReportByMetadata={assetVerificationReportByMetadata}
      onEdit={onEdit}
    />
  );
};

const VerificationAssetVerificationSectionTableStored: React.FC<{
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
  artifactDocumentId: string;
  loanProductId?: string;
  fileVersion: number;
  adminMode: boolean;
  onEdit?: EditAssetVerification;
}> = ({
  creditFacilityId,
  reportingDate,
  fileVersion,
  artifactDocumentId,
  loanProductId,
  adminMode,
  onEdit = () => {},
}) => {
  const {
    data,
    loading,
  } = useGetStoredAssetVerificationReceivablesByMetadataQuery({
    fetchPolicy: 'network-only',
    variables: {
      creditFacilityId,
      reportingDate,
      artifactDocumentId,
      loanProductId,
      fileVersion,
    },
  });

  const assetVerificationReportByMetadata = loading
    ? null
    : data?.assetVerificationReportByMetadataStored;

  return (
    <VerificationAssetVerificationSectionTable
      creditFacilityId={creditFacilityId}
      artifactDocumentId={artifactDocumentId}
      adminMode={adminMode}
      reportingDate={reportingDate}
      fileVersion={fileVersion}
      assetVerificationReportByMetadata={assetVerificationReportByMetadata}
      onEdit={onEdit}
    />
  );
};

export const VerificationAssetVerificationSection: React.FC<{
  artifactDocumentId: string;
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  dynamicMode: boolean;
  adminMode?: boolean;
  loanProductId?: string;
  onEdit?: EditAssetVerification;
}> = ({
  artifactDocumentId,
  creditFacilityId,
  reportingDate,
  fileVersion,
  loanProductId,
  onEdit,
  adminMode = false,
  dynamicMode = false,
}) => {
  const commonProps = {
    artifactDocumentId,
    creditFacilityId,
    reportingDate,
    fileVersion,
    onEdit,
    adminMode,
    loanProductId,
  };
  return (
    <CheckAssetVerificationRequired
      artifactDocumentId={artifactDocumentId}
      creditFacilityId={creditFacilityId}
    >
      {dynamicMode ? (
        <VerificationAssetVerificationSectionTableDynamic {...commonProps} />
      ) : (
        <VerificationAssetVerificationSectionTableStored {...commonProps} />
      )}
    </CheckAssetVerificationRequired>
  );
};
